import { useContext, useState } from "react"
import { studentRequest } from "services/requests"
import { AuthContext } from "contexts/AuthContext"
import { newSpeechServiceURL, speechServiceURL } from "services/config"
import { useGlobalAlert } from "contexts/GlobalAlertContext"
import { DEVICE_TYPES, ENVIORNMENTS } from "common/constants"
import { t } from "i18next"
const FormData = require("form-data")

const useApi = () => {
  const [isFetching, setIsFetching] = useState(false)
  const { selectedGroupData } = useContext(AuthContext)
  const { showAlert } = useGlobalAlert()
  const groupId = selectedGroupData?.groupId || -1
  /** ONLYFor development purposes use "ignoreRetakeLimit": true to ignore this limit **/
  const ignoreRetakeLimit =
    process.env.REACT_APP_ENVIORNMENT === ENVIORNMENTS.DEVELOPMENT

  const getMissionById = async ({ missionId, loadModules = true }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `missions/find/${missionId}/${loadModules}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(t(response.message) || t("Something went wrong"), teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getGroupMissions = async (
    data = { group: groupId, filter: "all", page: 1 }
  ) => {
    const { group, filter, page } = data
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `missions/${group}/${filter}/${page}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(t(response.message) || t("Something went wrong"), teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getMissionsCount = async ({ groupId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `missions/filters/${groupId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(t(response.message) || t("Something went wrong"), teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getMissionModules = async ({ missionId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `missions/modules/${missionId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(t(response.message) || t("Something went wrong"), teacherImage)
    setIsFetching(false)
    return response?.data || []
  }

  const getExercises = async ({ missionModuleId, activityId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `activityContent/exercise/${missionModuleId}/${activityId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(t(response.message) || t("Something went wrong"), teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getActivities = async ({
    group = groupId,
    missionId,
    missionModuleId,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `activityContent/module/${group}/${missionId}/${missionModuleId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(t(response.message) || t("Something went wrong"), teacherImage)
    setIsFetching(false)
    return response.data
  }

  const setGrade = async ({
    grade,
    exerciseId,
    professorModuleActivityId,
    answers,
    filesFromPartialGrade = false,
    metadata = "",
    activityTeamId,
    streak = null,
  }) => {
    setIsFetching(true)
    const deviceTypeId = DEVICE_TYPES.WEB
    const response = await studentRequest.sendRequest({
      endpoint: `grades/add`,
      body: {
        grade: parseInt(grade),
        ignoreRetakeLimit,
        exerciseId,
        professorModuleActivityId,
        answers,
        filesFromPartialGrade,
        metadata,
        deviceTypeId,
        streak,
        ...(activityTeamId !== undefined && { activityTeamId }),
      },
      method: "post",
    })
    if (!response.success)
      console.log(t(response.message) || t("Something went wrong"))
    setIsFetching(false)
    return response
  }

  const uploadGradeFile = async ({ gradeId, files, onUploadProgress }) => {
    setIsFetching(true)
    const data = new FormData()
    for (const audioFile of files) {
      data.append("files", audioFile)
    }
    const response = await studentRequest.sendFormData({
      endpoint: `grades/upload/${gradeId}`,
      formData: data,
      onUploadProgress,
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response) showAlert(t("unexpected error"), teacherImage)
    setIsFetching(false)
    return response?.data
  }

  const getContentByGrade = async ({ gradeId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `activityContent/grade/${gradeId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(t(response.message) || t("Something went wrong"), teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getMissionFeedback = async ({ missionId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `missions/feedback/${missionId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(t(response.message) || t("Something went wrong"), teacherImage)
    setIsFetching(false)
    return response.data
  }

  const getSpeechRecognitionValue = async ({
    audioFile,
    languageId,
    options,
    signal,
    speed = 2,
    attemp = 1,
    speechAvailable = true,
    serviceEngine = "auto",
  }) => {
    setIsFetching(true)
    const data = new FormData()
    data.append("file", audioFile)
    data.append("audio", audioFile)
    data.append("phrase", options[0]?.text || "")
    data.append("culture", languageId)
    data.append("options", JSON.stringify(options))
    data.append("sequence", "0")
    data.append("speed", speed)
    data.append("reduceNoise", false)
    data.append("trimNoise", false)
    console.log({
      audioFile,
      languageId,
      options,
      speed,
      attemp,
      speechAvailable,
    })
    const useNewSpeechService =
      serviceEngine === "new" || (serviceEngine === "auto" && attemp > 1)
    const url = useNewSpeechService ? newSpeechServiceURL : speechServiceURL
    try {
      console.log(`fetching ${url}`)
      const response = await fetch(url, {
        method: "POST",
        body: data,
        signal, // Incluir la señal aquí
      })
      const result = await response.json()
      setIsFetching(false)
      if (!useNewSpeechService) {
        const success = result?.status === "success"
        result.status = true
        if (success) {
          result.finalResult = {
            transcript: options[0]?.text || "",
            similarity: 1,
          }
        }
        result.alternatives =
          result?.data?.alternatives?.length > 0
            ? result?.data?.alternatives?.map((alternative) => {
                return {
                  transcript: alternative,
                  similarity: 0,
                }
              })
            : []
      }
      return result
    } catch (e) {
      setIsFetching(false)
      // if error code is 404
      if (e?.status !== 200) {
        console.log("Speech recognition service not available")
        return {
          status: false,
          finalResult: null,
          alternatives: [],
        }
      }

      if (e.name === "AbortError") {
        console.log("Fetch aborted")
      } else {
        const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
        showAlert(t(e.message) || t("Something went wrong"), teacherImage)
      }
    }
  }

  const getModuleSkills = async ({ missionModuleId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `activityContent/module/skills/${missionModuleId}`,
      body: null,
      method: "get",
    })
    const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
    if (!response.success)
      showAlert(t(response.message) || t("Something went wrong"), teacherImage)
    setIsFetching(false)
    return response.data
  }

  const addPartialGrade = async ({
    exerciseId,
    professorModuleActivityId = 0,
    answer,
    metadata = "",
    audioFile,
    onUploadProgress,
    placementTestId = 0,
  }) => {
    const deviceTypeId = DEVICE_TYPES.WEB
    const data = new FormData()
    if (professorModuleActivityId > 0) {
      data.append("professorModuleActivityId", professorModuleActivityId)
    }
    data.append("exerciseId", exerciseId)
    data.append("metadata", metadata)
    data.append("deviceTypeId", deviceTypeId)
    if (placementTestId > 0) {
      data.append("placementTestId", placementTestId)
    }
    if (answer) {
      data.append("answer.ContentId", answer.contentId)
      data.append("answer.Answer", answer.answer)
      data.append("answer.Status", answer.status)
      if (answer.metadata) {
        data.append("answer.Metadata", answer.metadata)
      }
    }
    if (audioFile) {
      data.append("audioFile", audioFile)
    }
    const response = await studentRequest.sendFormData({
      endpoint: `grades/partial/add`,
      formData: data,
      onUploadProgress,
    })
    if (!response.success)
      console.log(response.message || "Something went wrong")
    setIsFetching(false)
    return response
  }

  const addPartialGradeAll = async ({
    exerciseId,
    professorModuleActivityId,
    answers = null,
    metadata = "",
    audioFile,
    onUploadProgress,
    placementTestId = 0,
  }) => {
    const deviceTypeId = DEVICE_TYPES.WEB
    const data = {
      // professorModuleActivityId,
      exerciseId,
      metadata,
      answers,
      deviceTypeId,
    }
    if (placementTestId > 0) {
      data.placementTestId = placementTestId
    }
    if (professorModuleActivityId > 0) {
      data.professorModuleActivityId = professorModuleActivityId
    }
    const response = await studentRequest.sendRequest({
      endpoint: `grades/partial/addAll`,
      body: data,
      method: "post",
    })
    if (!response.success)
      console.log(response.message || "Something went wrong")
    setIsFetching(false)
    return response
  }

  const deletePartialGrade = async ({ partialGradeId }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `grades/partial/${partialGradeId}`,
      body: null,
      method: "delete",
    })
    if (!response.success)
      console.log(response.message || "Something went wrong")
    setIsFetching(false)
    return response
  }

  const getActivityTeam = async ({
    groupId,
    missionsActivityId,
    exerciseId,
    joinTeam = false,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `activityContent/activityTeam`,
      body: {
        groupId,
        missionsActivityId,
        exerciseId,
        joinTeam,
      },
      method: "post",
    })
    if (!response.success)
      console.log(response.message || "Something went wrong")
    setIsFetching(false)
    return response.data
  }

  const setUserAlert = async ({
    groupId,
    userAlertTypeId = 1,
    seen = true,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `userAlerts`,
      body: {
        groupId,
        userAlertTypeId,
        seen,
      },
      method: "post",
    })
    if (!response.success)
      console.log(response.message || "Something went wrong")
    setIsFetching(false)
    return response
  }

  return {
    isFetching,
    getActivities,
    getExercises,
    getGroupMissions,
    getMissionModules,
    setGrade,
    uploadGradeFile,
    getMissionById,
    getContentByGrade,
    getMissionFeedback,
    getSpeechRecognitionValue,
    getModuleSkills,
    addPartialGrade,
    deletePartialGrade,
    getMissionsCount,
    addPartialGradeAll,
    getActivityTeam,
    setUserAlert,
  }
}

export default useApi
